import {useCallback, useMemo} from 'react';
import {toast} from 'react-toastify';
import {createColumnHelper, getCoreRowModel, useReactTable} from '@tanstack/react-table';
import {EnvelopeIcon, TrashIcon} from '@heroicons/react/24/outline';
import {getGetInvitationsQueryKey, useGetInvitations, useResendInvitation, useRevokeInvitation, type InvitationType} from 'api/generated';
import {format} from 'date-fns';
import {cacheUpdateItem, classNames, getInitials} from 'utils';
import {Avatar, Dropdown} from 'components';

const columnHelper = createColumnHelper<InvitationType>();

const INVITATION_STATE_COLOR: Record<InvitationType['state'], string> = {
  pending: '',
  viewed: 'text-orange-400 dark:text-yellow-500',
  accepted: 'text-green-600 dark:text-green-500',
  revoked: 'text-red-500 dark:text-red-400',
};

interface IUseTableInvitesProps {
  filter: 'people' | 'invites';
}

const useTableInvites = ({filter}: IUseTableInvitesProps) => {
  const {data: invitesData} = useGetInvitations({query: {enabled: filter === 'invites', staleTime: 10 * 1000}});
  const invites = useMemo(() => invitesData?.data?.data ?? [], [invitesData?.data?.data]);

  const resendInvitation = useResendInvitation();
  const handleResend = useCallback(async (uid: string) => {
    const {data: res} = await resendInvitation.mutateAsync({invitationUid: uid});
    if (res.data) {
      toast.success('Invitation resent');
      cacheUpdateItem({
        queryKey: getGetInvitationsQueryKey(),
        data: res.data,
      });
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  }, []);

  const revokeInvitation = useRevokeInvitation();
  const handleRevoke = useCallback(async (uid: string) => {
    const {data: res} = await revokeInvitation.mutateAsync({uid});
    if (res.data) {
      toast.success('Invitation revoked');
      cacheUpdateItem({
        queryKey: getGetInvitationsQueryKey(),
        data: res.data,
      });
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: () => <span>Members</span>,
        cell: info => (
          <div className="flex gap-3 items-center">
            <Avatar alt={info.getValue()} fallback={getInitials(info.getValue())} />
            <div className="flex flex-col">
              <span> {info.getValue()}</span>
              <span className="text-slate-500 dark:text-slate-400 capitalize mt-2 text-xs">{info.row.original.role}</span>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('state', {
        header: () => <span>State</span>,
        cell: info => <span className={classNames('capitalize', INVITATION_STATE_COLOR[info.getValue()])}>{info.getValue()}</span>,
      }),
      columnHelper.accessor('createdAt', {
        header: () => <span>Sent</span>,
        cell: info => <span>{info.getValue() ? format(new Date(info.getValue()!), 'dd MMM y') : '-'}</span>,
      }),
      columnHelper.accessor('resentAt', {
        header: () => <span>Resent</span>,
        cell: info => <span>{info.getValue() ? format(new Date(info.getValue()!), 'dd MMM y') : '-'}</span>,
      }),
      columnHelper.accessor('acceptedAt', {
        header: () => <span>Accepted</span>,
        cell: info => <span>{info.getValue() ? format(new Date(info.getValue()!), 'dd MMM y') : '-'}</span>,
      }),
      columnHelper.accessor('metadata', {
        header: () => <span>To collab</span>,
        cell: info => {
          const projectsCount = info.getValue()?.projectUids?.length;
          const noRef = !projectsCount;
          return (
            <span
              className={classNames(!noRef && 'text-primary hover:brightness-120 cursor-pointer')}
              // TODO: Implement this
              onClick={() => !noRef && console.info('show linked projects modal')}>
              {noRef ? '-' : `${projectsCount} projects`}
            </span>
          );
        },
      }),
      columnHelper.accessor('uid', {
        header: () => <span></span>,
        cell: info => {
          const isAccepted = info.row.original.state === 'accepted';
          const isRevoked = info.row.original.state === 'revoked';
          if (isAccepted || isRevoked) {
            return null;
          }
          const dropdownItems = [
            [{icon: EnvelopeIcon, text: 'Resend', onClick: () => handleResend(info.row.original.uid)}],
            [{icon: TrashIcon, text: 'Revoke', onClick: () => handleRevoke(info.row.original.uid), color: 'text-red-500'}],
          ];
          return (
            <div className="shrink-0 pr-2">
              <Dropdown items={dropdownItems} />
            </div>
          );
        },
      }),
    ],
    [handleResend, handleRevoke],
  );

  const table = useReactTable({
    data: invites,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return table;
};

export default useTableInvites;
